import type { AdminAssistantData, AdminAssistantRun, AdminIdentity, AdminPlanNote, AdminUatData, AdminUatFile, AdminUatItem, AdminUatNote, AdminUatProject, UatItem, UatNote, UatNoteSourceWorkspace, UatProject, UatQuestion, UatSyncSummary, UatView, AdminWorkspace, PlanNote, PlanTask, ResponsePlanSummary, ResponsePlanView, ThreadReply, CatalogItem, CatalogResponse, ContractRow, ContractSigner, CrmOption, CrmRecordKind, DealDetails, DealFollowUpDraft, DealQualificationDraft, FollowUpWarning, OrderForm, PricingModel, StripeBillingPreview, PublicWorkspace, SharedSandboxAccess, TimelineEvent, WorkspaceListPage, WorkspacePhase, WorkspaceQuote } from '../types'
import type { CustomerDetails } from './order-form'
import type { UatCaseResult, UatCustomerStatus, UatFileGate, UatFileVisibility, UatRequestType, UatStatus } from './uat'
import type { UatNoteStatus } from './uat-notes'

async function readResponsePayload(response: Response): Promise<Record<string, unknown>> {
  const raw = await response.text().catch(() => '')
  if (!raw) return {}
  try {
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {}
  } catch {
    return {}
  }
}

export interface StandardDocumentStatus {
  source: 'uploaded' | 'builtin'
  meta: { fileName: string; byteSize: number; uploadedBy: string; uploadedAt: string } | null
  url: string
}

/** The Prentus signer's saved signature. The image itself is at SAVED_SIGNATURE_URL (admin session only). */
export interface SavedSignatureStatus { signerEmail: string; signerName: string; uploadedBy: string; uploadedAt: string; byteSize: number; matchesSigner: boolean }
export const SAVED_SIGNATURE_URL = '/api/admin/contract-signer/signature'

export interface OrderFormInvoiceLinks {
  number: string | null
  status: string | null
  hostedInvoiceUrl: string
  invoicePdf: string | null
  amountDue: number | null
  dueDate: string | null
}

export async function request<T>(path: string, init?: RequestInit, unexpectedResponseMessage = 'Something went wrong.'): Promise<T> {
  const hasJsonBody = Boolean(init?.body) && !(init?.body instanceof FormData)
  const response = await fetch(path, {
    credentials: 'same-origin',
    cache: 'no-store',
    ...init,
    headers: {
      ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  })
  const data = await readResponsePayload(response)
  if (!response.ok) {
    const error = new Error(typeof data.error === 'string' ? data.error : unexpectedResponseMessage) as Error & { status?: number; code?: string; warnings?: FollowUpWarning[] }
    error.status = response.status
    if (typeof data.code === 'string') error.code = data.code
    if (Array.isArray(data.warnings)) error.warnings = data.warnings
    throw error
  }
  return data as T
}

export const api = {
  findWorkspace(email: string) {
    return request<{ kind: 'admin'; redirectTo: string } | { kind: 'customer'; slug: string } | { kind: 'partner'; slug: string }>('/api/access/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },
  loadAdminWorkspaces(params: { page?: number; pageSize?: number; search?: string; status?: 'draft' | 'published' } = {}) {
    const query = new URLSearchParams()
    if (params.page) query.set('page', String(params.page))
    if (params.pageSize) query.set('pageSize', String(params.pageSize))
    if (params.search) query.set('search', params.search)
    if (params.status) query.set('status', params.status)
    const suffix = query.toString()
    return request<WorkspaceListPage>(`/api/admin/workspaces${suffix ? `?${suffix}` : ''}`)
  },
  searchCrm(kind: CrmRecordKind, query: string) {
    return request<{ results: CrmOption[] }>('/api/admin/crm/search', {
      method: 'POST',
      body: JSON.stringify({ kind, query }),
    })
  },
  loadDealDetails(dealId: string) {
    return request<DealDetails>('/api/admin/crm/deal-details', {
      method: 'POST',
      body: JSON.stringify({ dealId }),
    })
  },
  createWorkspaceFromDeal(payload: { dealId: string; primaryCompanyId: string; companyIds: string[]; contactIds: string[] }) {
    return request<{ workspaceId: string; publicId: string; publicUrl: string; status: string; callsImported: number }>('/api/admin/workspaces/from-deal', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },
  createWorkspaceFromCompany(payload: { companyId: string; companyName: string; companyDomain?: string; phase?: WorkspacePhase; contacts: Array<{ id?: string; email: string; name?: string; title?: string }> }) {
    return request<{ workspaceId: string; publicId: string; publicUrl: string; status: string; callsImported: number }>('/api/admin/workspaces/from-company', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },
  loadWorkspace(publicId: string) {
    return request<PublicWorkspace>(`/api/workspaces/${encodeURIComponent(publicId)}`)
  },
  requestCode(publicId: string, email: string) {
    return request<{ ok: true }>(`/api/workspaces/${encodeURIComponent(publicId)}/access/request`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },
  verifyCode(publicId: string, email: string, code: string) {
    return request<PublicWorkspace>(`/api/workspaces/${encodeURIComponent(publicId)}/access/verify`, {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    })
  },
  track(publicId: string, eventType: string, targetId?: string) {
    return request<{ ok: true }>(`/api/workspaces/${encodeURIComponent(publicId)}/events`, {
      method: 'POST',
      body: JSON.stringify({ eventType, targetId }),
    })
  },
  reportOrderFormActivity(publicId: string, formId: string, type: 'viewed' | 'signing_started') {
    return request<{ ok: true }>(`/api/workspaces/${encodeURIComponent(publicId)}/order-forms/${encodeURIComponent(formId)}/activity`, {
      method: 'POST',
      body: JSON.stringify({ type }),
    })
  },
  requestSandbox(publicId: string, context: string) {
    return request<{ id: string; status: 'pending' }>(`/api/workspaces/${encodeURIComponent(publicId)}/sandbox-requests`, {
      method: 'POST',
      body: JSON.stringify({ context }),
    })
  },
  /** The native Response Plan tab: sections, tasks, notes with replies, and what this viewer can do. */
  loadResponsePlan(publicId: string) {
    return request<ResponsePlanView>(`/api/workspaces/${encodeURIComponent(publicId)}/response-plan`)
  },
  sendPlanNote(publicId: string, sectionKey: string, body: string) {
    return request<{ note: PlanNote; assistantRunId: string | null }>(`/api/workspaces/${encodeURIComponent(publicId)}/response-plan/notes`, {
      method: 'POST',
      body: JSON.stringify({ sectionKey, body }),
    }, 'The note could not be sent.')
  },
  setPlanTaskDone(publicId: string, taskId: string, done: boolean) {
    return request<{ task: PlanTask }>(`/api/workspaces/${encodeURIComponent(publicId)}/response-plan/tasks/${encodeURIComponent(taskId)}`, {
      method: 'POST',
      body: JSON.stringify({ done }),
    }, 'The task could not be saved.')
  },
  /** A reply on a plan note or a UAT entry, from either side. */
  sendReply(publicId: string, parent: { type: 'plan-note' | 'uat-event'; id: string }, body: string) {
    return request<{ reply: ThreadReply; assistantRunId: string | null }>(`/api/workspaces/${encodeURIComponent(publicId)}/threads/${parent.type}/${encodeURIComponent(parent.id)}/replies`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }, 'The reply could not be sent.')
  },
  /** Prentus admins: correct or hide a Prentus Assistant reply. */
  moderateReply(publicId: string, replyId: string, change: { body?: string; hidden?: boolean }) {
    return request<{ reply: ThreadReply }>(`/api/workspaces/${encodeURIComponent(publicId)}/replies/${encodeURIComponent(replyId)}`, {
      method: 'PATCH',
      body: JSON.stringify(change),
    }, 'The reply could not be changed.')
  },
  /** Waits for the Prentus Assistant's answer. It can take a minute. */
  runAssistant(publicId: string, runId: string) {
    return request<{ status: AdminAssistantRun['status'] }>(`/api/workspaces/${encodeURIComponent(publicId)}/assistant/runs/${encodeURIComponent(runId)}`, { method: 'POST' }, 'The Prentus Assistant could not answer.')
  },
  loadAdminResponsePlan(workspaceId: string) {
    return request<{ plan: ResponsePlanSummary | null; notes: AdminPlanNote[]; linearKeyConfigured: boolean }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/response-plan`)
  },
  savePlanSectionSpec(workspaceId: string, sectionKey: string, spec: string) {
    return request<{ ok: true; spec: string }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/response-plan/sections/${encodeURIComponent(sectionKey)}`, { method: 'PATCH', body: JSON.stringify({ spec }) }, 'The spec could not be saved.')
  },
  removeResponsePlan(workspaceId: string) {
    return request<{ ok: true }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/response-plan`, { method: 'DELETE' })
  },
  syncPlanNotes(workspaceId: string) {
    return request<{ synced: number; pending: number }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/response-plan/sync`, { method: 'POST' })
  },
  loadAdminAssistant(workspaceId: string) {
    return request<AdminAssistantData>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/assistant`)
  },
  setAssistantEnabled(workspaceId: string, enabled: boolean) {
    return request<AdminAssistantData>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/assistant`, { method: 'PUT', body: JSON.stringify({ enabled }) }, 'The Assistant setting could not be saved.')
  },
  /** The UAT tab. canEdit is true only for a verified customer. */
  loadUat(publicId: string) {
    return request<UatView>(`/api/workspaces/${encodeURIComponent(publicId)}/uat`)
  },
  askUatQuestion(publicId: string, body: string) {
    return request<{ questions: UatQuestion[]; eventId: string; assistantRunId: string | null }>(`/api/workspaces/${encodeURIComponent(publicId)}/uat/questions`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }, 'The question could not be sent.')
  },
  /** A customer adds an Issue Found, a Question or a Request. */
  addCustomerUatItem(publicId: string, input: { type: UatRequestType; title: string; details: string; projectId: string | null }) {
    return request<{ item: UatItem; projects: UatProject[]; eventId: string; assistantRunId: string | null }>(`/api/workspaces/${encodeURIComponent(publicId)}/uat/items`, {
      method: 'POST',
      body: JSON.stringify(input),
    }, 'The item could not be added.')
  },
  setUatSubscription(publicId: string, subscribed: boolean) {
    return request<{ subscription: { subscribed: boolean } }>(`/api/workspaces/${encodeURIComponent(publicId)}/uat/subscription`, {
      method: 'POST',
      body: JSON.stringify({ subscribed }),
    }, 'The email setting could not be saved.')
  },
  setUatStatus(publicId: string, itemId: string, status: UatCustomerStatus, note?: string) {
    return request<{ item: UatItem }>(`/api/workspaces/${encodeURIComponent(publicId)}/uat/items/${encodeURIComponent(itemId)}/status`, {
      method: 'POST',
      body: JSON.stringify(note ? { status, note } : { status }),
    }, 'The status could not be changed.')
  },
  /** A comment on an item. It goes to the Prentus team. */
  addUatNote(publicId: string, itemId: string, body: string) {
    return request<{ item: UatItem; eventId?: string; assistantRunId?: string | null }>(`/api/workspaces/${encodeURIComponent(publicId)}/uat/items/${encodeURIComponent(itemId)}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }, 'The comment could not be sent.')
  },
  setUatCaseResult(publicId: string, caseId: string, result: UatCaseResult, note?: string) {
    return request<{ item: UatItem }>(`/api/workspaces/${encodeURIComponent(publicId)}/uat/cases/${encodeURIComponent(caseId)}/result`, {
      method: 'POST',
      body: JSON.stringify(note ? { result, note } : { result }),
    }, 'The result could not be saved.')
  },
  loadAdminUat(workspaceId: string) {
    return request<AdminUatData>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat`)
  },
  createUatProject(workspaceId: string, input: { name: string; description?: string; targetDate?: string | null; linearIssue?: string | null }) {
    return request<{ project: AdminUatProject }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/projects`, { method: 'POST', body: JSON.stringify(input) }, 'The project could not be added.')
  },
  updateUatProject(workspaceId: string, projectId: string, input: Partial<{ name: string; description: string; targetDate: string | null; linearIssue: string | null }>) {
    return request<{ project: AdminUatProject }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/projects/${encodeURIComponent(projectId)}`, { method: 'PATCH', body: JSON.stringify(input) }, 'The project could not be saved.')
  },
  removeUatProject(workspaceId: string, projectId: string) {
    return request<{ ok: true }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/projects/${encodeURIComponent(projectId)}`, { method: 'DELETE' }, 'The project could not be removed.')
  },
  reorderUatProjects(workspaceId: string, ids: string[]) {
    return request<{ ok: true }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/projects/reorder`, { method: 'POST', body: JSON.stringify({ ids }) }, 'The order could not be saved.')
  },
  createUatItem(workspaceId: string, input: { title: string; description?: string; spec?: string; linearIssue?: string | null; deliveredOn?: string | null; targetDate?: string | null; status?: UatStatus; projectId?: string | null }) {
    return request<{ item: AdminUatItem }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/items`, { method: 'POST', body: JSON.stringify(input) }, 'The item could not be added.')
  },
  updateUatItem(workspaceId: string, itemId: string, input: Partial<{ title: string; description: string; spec: string; linearIssue: string | null; deliveredOn: string | null; targetDate: string | null; status: UatStatus; projectId: string | null }>) {
    return request<{ item: AdminUatItem }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/items/${encodeURIComponent(itemId)}`, { method: 'PATCH', body: JSON.stringify(input) }, 'The item could not be saved.')
  },
  mergeUatItem(workspaceId: string, itemId: string, targetId: string) {
    return request<{ item: AdminUatItem }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/items/${encodeURIComponent(itemId)}/merge`, { method: 'POST', body: JSON.stringify({ targetId }) }, 'The items could not be merged.')
  },
  removeUatItem(workspaceId: string, itemId: string) {
    return request<{ ok: true }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/items/${encodeURIComponent(itemId)}`, { method: 'DELETE' }, 'The item could not be removed.')
  },
  reorderUatItems(workspaceId: string, ids: string[]) {
    return request<{ ok: true }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/reorder`, { method: 'POST', body: JSON.stringify({ ids }) }, 'The order could not be saved.')
  },
  addUatCase(workspaceId: string, itemId: string, text: string, area: string | null) {
    return request<{ item: AdminUatItem }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/items/${encodeURIComponent(itemId)}/cases`, { method: 'POST', body: JSON.stringify({ text, area }) }, 'The test case could not be added.')
  },
  importUatCases(workspaceId: string, itemId: string, csv: string, mode: 'append' | 'replace') {
    return request<{ item: AdminUatItem; imported: number; skipped: number; caseColumn: string | null; areaColumn: string | null }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/items/${encodeURIComponent(itemId)}/cases/import`, { method: 'POST', body: JSON.stringify({ csv, mode }) }, 'The test cases could not be imported.')
  },
  removeUatCase(workspaceId: string, caseId: string) {
    return request<{ ok: true }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/cases/${encodeURIComponent(caseId)}`, { method: 'DELETE' }, 'The test case could not be removed.')
  },
  /** A new download on a project or an item. */
  uploadUatFile(workspaceId: string, input: { file: File; title: string; note: string; versionLabel: string; visibility: UatFileVisibility; gateType: UatFileGate; gateIssues: string; projectId: string | null; itemId: string | null }) {
    const form = new FormData()
    form.set('file', input.file)
    form.set('title', input.title)
    form.set('note', input.note)
    form.set('versionLabel', input.versionLabel)
    form.set('visibility', input.visibility)
    form.set('gateType', input.gateType)
    form.set('gateIssues', input.gateIssues)
    if (input.projectId) form.set('projectId', input.projectId)
    if (input.itemId) form.set('itemId', input.itemId)
    return request<{ file: AdminUatFile }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/files`, { method: 'POST', body: form }, 'The file could not be uploaded.')
  },
  /** A new version of a download. The old version stays, hidden. */
  uploadUatFileVersion(workspaceId: string, fileId: string, file: File, versionLabel: string) {
    const form = new FormData()
    form.set('file', file)
    form.set('versionLabel', versionLabel)
    return request<{ file: AdminUatFile }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/files/${encodeURIComponent(fileId)}/versions`, { method: 'POST', body: form }, 'The new version could not be uploaded.')
  },
  updateUatFile(workspaceId: string, fileId: string, input: Partial<{ title: string; note: string; versionLabel: string; visibility: UatFileVisibility; gateType: UatFileGate; gateIssues: string[] }>) {
    return request<{ file: AdminUatFile }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/files/${encodeURIComponent(fileId)}`, { method: 'PATCH', body: JSON.stringify(input) }, 'The file could not be saved.')
  },
  releaseUatFile(workspaceId: string, fileId: string) {
    return request<{ file: AdminUatFile }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/files/${encodeURIComponent(fileId)}/release`, { method: 'POST' }, 'The file could not be released.')
  },
  /** A customer comment on a What's Changing section. */
  addUatNoteComment(publicId: string, noteId: string, body: string) {
    return request<{ note: UatNote | null; eventId: string; assistantRunId: string | null }>(`/api/workspaces/${encodeURIComponent(publicId)}/uat/notes/${encodeURIComponent(noteId)}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }, 'The comment could not be sent.')
  },
  createUatNote(workspaceId: string, input: { projectId: string; title: string }) {
    return request<{ note: AdminUatNote }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/notes`, { method: 'POST', body: JSON.stringify(input) }, 'The section could not be added.')
  },
  updateUatNote(workspaceId: string, noteId: string, input: Partial<{ title: string; key: string | null; whatMembersSee: string; whatChanged: string; studentVsJobseeker: string; schoolNotes: string; status: UatNoteStatus; itemIds: string[]; imageIds: string[] }>) {
    return request<{ note: AdminUatNote }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/notes/${encodeURIComponent(noteId)}`, { method: 'PATCH', body: JSON.stringify(input) }, 'The section could not be saved.')
  },
  removeUatNote(workspaceId: string, noteId: string) {
    return request<{ ok: true }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/notes/${encodeURIComponent(noteId)}`, { method: 'DELETE' }, 'The section could not be removed.')
  },
  reorderUatNotes(workspaceId: string, projectId: string, ids: string[]) {
    return request<{ ok: true }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/notes/reorder`, { method: 'POST', body: JSON.stringify({ projectId, ids }) }, 'The order could not be saved.')
  },
  /** A screenshot from a file or the clipboard. */
  uploadUatNoteImage(workspaceId: string, noteId: string, file: File, caption: string) {
    const form = new FormData()
    form.set('file', file)
    form.set('caption', caption)
    return request<{ note: AdminUatNote }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/notes/${encodeURIComponent(noteId)}/images`, { method: 'POST', body: form }, 'The screenshot could not be uploaded.')
  },
  updateUatNoteImage(workspaceId: string, imageId: string, caption: string) {
    return request<{ note: AdminUatNote }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/note-images/${encodeURIComponent(imageId)}`, { method: 'PATCH', body: JSON.stringify({ caption }) }, 'The caption could not be saved.')
  },
  removeUatNoteImage(workspaceId: string, imageId: string) {
    return request<{ note: AdminUatNote }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/note-images/${encodeURIComponent(imageId)}`, { method: 'DELETE' }, 'The screenshot could not be removed.')
  },
  loadUatNoteSources(excludeWorkspaceId: string) {
    return request<{ workspaces: UatNoteSourceWorkspace[] }>(`/api/admin/uat/note-sources?exclude=${encodeURIComponent(excludeWorkspaceId)}`)
  },
  copyUatNotes(workspaceId: string, projectId: string, sourceProjectId: string, noteIds: string[]) {
    return request<{ copied: number; skipped: string[]; images: number; links: number; notes: AdminUatNote[] }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/projects/${encodeURIComponent(projectId)}/notes/copy`, { method: 'POST', body: JSON.stringify({ sourceProjectId, noteIds }) }, 'The sections could not be copied.')
  },
  /** Sync Now: pending customer entries to Linear, then the ship sync. */
  syncUat(workspaceId: string) {
    return request<UatSyncSummary>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uat/sync`, { method: 'POST' })
  },
  loadSandboxAccess(publicId: string) {
    return request<SharedSandboxAccess>(`/api/workspaces/${encodeURIComponent(publicId)}/sandbox-access`)
  },
  generateQuote(publicId: string, pricingModel: PricingModel, calculatorInputs: Record<string, unknown>) {
    return request<WorkspaceQuote>(`/api/workspaces/${encodeURIComponent(publicId)}/quotes`, {
      method: 'POST',
      body: JSON.stringify({ requestId: crypto.randomUUID(), pricingModel, calculatorInputs }),
    })
  },
  recordSession(publicId: string, payload: {
    action: 'start' | 'heartbeat' | 'end'
    sessionId: string
    sectionKey?: string
    durationMs?: number
    click?: { type: 'asset_open' | 'asset_download'; targetId: string }
  }) {
    return request<{ ok: true }>(`/api/workspaces/${encodeURIComponent(publicId)}/session`, {
      method: 'POST',
      body: JSON.stringify(payload),
      keepalive: true,
    })
  },
  loadCatalog() {
    return request<CatalogResponse>('/api/admin/catalog')
  },
  saveCatalog(items: CatalogItem[]) {
    return request<CatalogResponse>('/api/admin/catalog', { method: 'PUT', body: JSON.stringify(items) })
  },
  sendOrderFormForSignature(workspaceId: string, formId: string, payload: { cc: string[]; note: string | null; emailCustomer: boolean }) {
    return request<{ orderForm: OrderForm; ccNotified: boolean; senderSigns: boolean; prentusEmailed: boolean; autoSigned: boolean; customerEmailed: boolean }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/order-forms/${encodeURIComponent(formId)}/send`, { method: 'POST', body: JSON.stringify(payload) })
  },
  /** The customer corrects their address, billing contact or PO number on a form out for signature. */
  updateOrderFormDetails(publicId: string, formId: string, details: Partial<CustomerDetails>) {
    return request<{ orderForm: OrderForm; changed: boolean; autoSigned?: boolean }>(`/api/workspaces/${encodeURIComponent(publicId)}/order-forms/${encodeURIComponent(formId)}/customer-details`, { method: 'POST', body: JSON.stringify(details) })
  },
  remindOrderFormSigner(workspaceId: string, formId: string) {
    return request<{ orderForm: OrderForm }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/order-forms/${encodeURIComponent(formId)}/remind`, { method: 'POST', body: '{}' })
  },
  /** Cancel and Edit (draft) or Void. Cancels the DocuSeal request first. */
  withdrawOrderForm(workspaceId: string, formId: string, status: 'draft' | 'void') {
    return request<{ orderForm: OrderForm }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/order-forms/${encodeURIComponent(formId)}/withdraw`, { method: 'POST', body: JSON.stringify({ status }) })
  },
  /** Whether Stripe is connected, and to the test or live account. Admin only. */
  loadStripeStatus() {
    return request<{ connected: boolean; mode: 'test' | 'live' | null }>('/api/admin/stripe/status')
  },
  /** Retry Stripe Setup on a signed form: runs only the steps that have not run yet. */
  retryStripeSetup(workspaceId: string, formId: string) {
    return request<{ orderForm: OrderForm }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/order-forms/${encodeURIComponent(formId)}/stripe/retry`, { method: 'POST', body: '{}' })
  },
  /** Stripe Billing check: what a setup would do for this form now. Stripe GET requests only; creates nothing. */
  loadStripePreview(workspaceId: string, formId: string) {
    return request<{ preview: StripeBillingPreview }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/order-forms/${encodeURIComponent(formId)}/stripe/preview`)
  },
  /** Active Stripe products for the product picker. */
  loadStripeProducts() {
    return request<{ products: Array<{ id: string; name: string }> }>('/api/admin/stripe/products')
  },
  /** Saves an admin's pick on the form: a Stripe product for a line, or the Stripe customer. Writes nothing to Stripe. */
  saveStripeSelection(workspaceId: string, formId: string, selection: { lineId: string; productId: string | null } | { customerId: string | null }) {
    return request<{ orderForm: OrderForm }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/order-forms/${encodeURIComponent(formId)}/stripe/selection`, { method: 'PUT', body: JSON.stringify(selection) })
  },
  /** The customer's invoice links for a signed form, once Stripe has issued the invoice. */
  loadOrderFormInvoice(publicId: string, formId: string) {
    return request<{ invoice: OrderFormInvoiceLinks | null }>(`/api/workspaces/${encodeURIComponent(publicId)}/order-forms/${encodeURIComponent(formId)}/invoice`)
  },
  loadStandardDocuments() {
    return request<{ csa: StandardDocumentStatus; signer: ContractSigner; signature: SavedSignatureStatus | null }>('/api/admin/documents')
  },
  saveSignature(png: string) {
    return request<{ signature: SavedSignatureStatus }>(SAVED_SIGNATURE_URL, { method: 'PUT', body: JSON.stringify({ png }) })
  },
  removeSignature() {
    return request<{ signature: null }>(SAVED_SIGNATURE_URL, { method: 'DELETE' })
  },
  uploadStandardCsa(file: File) {
    const form = new FormData()
    form.set('file', file)
    return request<{ csa: StandardDocumentStatus }>('/api/admin/documents/csa', { method: 'PUT', body: form, headers: {} })
  },
  saveContractSigner(signer: ContractSigner) {
    return request<{ signer: ContractSigner }>('/api/admin/contract-signer', { method: 'PUT', body: JSON.stringify(signer) })
  },
  removeStandardCsa() {
    return request<{ csa: StandardDocumentStatus }>('/api/admin/documents/csa', { method: 'DELETE' })
  },
  loadContracts() {
    return request<{ contracts: ContractRow[] }>('/api/admin/contracts')
  },
  setContractSharing(workspaceId: string, formId: string, shareSignedCopy: boolean) {
    return request<{ orderForm: OrderForm }>(`/api/admin/contracts/${encodeURIComponent(workspaceId)}/${encodeURIComponent(formId)}`, { method: 'PATCH', body: JSON.stringify({ shareSignedCopy }) })
  },
  /** Deletes an order form. A signed form needs its number as `confirmNumber`. */
  deleteContract(workspaceId: string, formId: string, confirmNumber?: string) {
    return request<{ ok: true }>(`/api/admin/contracts/${encodeURIComponent(workspaceId)}/${encodeURIComponent(formId)}`, { method: 'DELETE', body: JSON.stringify(confirmNumber ? { confirmNumber } : {}) })
  },
  loadAdminIdentity() {
    return request<AdminIdentity>('/api/admin/me')
  },
  /** Ends the admin session on the server and clears the admin cookie. */
  signOutAdmin() {
    return request<{ ok: true }>('/api/admin/auth/logout', { method: 'POST', body: '{}' })
  },
  addMember(workspaceId: string, payload: { side: 'customer' | 'prentus'; name: string; title: string | null; email: string; stakeholderRole: string | null }) {
    return request<{ id: string }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/members`, { method: 'POST', body: JSON.stringify(payload) })
  },
  setMemberHidden(workspaceId: string, memberId: string, hidden: boolean) {
    return request<{ ok: true }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(memberId)}`, { method: 'PATCH', body: JSON.stringify({ hidden }) })
  },
  loadAdminWorkspace(workspaceId: string) {
    return request<AdminWorkspace>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}`)
  },
  updateAdminWorkspace(workspaceId: string, patch: Record<string, unknown>) {
    return request<AdminWorkspace>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    })
  },
  decideApproval(workspaceId: string, payload: { itemType: 'asset' | 'business_case' | 'task' | 'suggestion' | 'sandbox_request'; itemId: string; decision: 'approve' | 'hide' }) {
    return request<AdminWorkspace>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/approvals`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },
  addCall(workspaceId: string, payload: { title: string; occurredAt: string; recordingUrl: string | null; description: string | null }) {
    return request<AdminWorkspace>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/calls`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },
  deleteWorkspaceItem(workspaceId: string, payload: { itemType: 'business_case' | 'task' | 'quote' | 'asset'; itemId: string }) {
    return request<{ ok: true }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/items`, {
      method: 'DELETE',
      body: JSON.stringify(payload),
    })
  },
  deleteWorkspace(workspaceId: string, payload: { accountName: string }) {
    return request<{ ok: true }>(
      `/api/admin/workspaces/${encodeURIComponent(workspaceId)}`,
      { method: 'DELETE', body: JSON.stringify(payload) },
      'Workspace deletion returned an unexpected response. Please try again.',
    )
  },
  reviewWorkspace(workspaceId: string) {
    return request<{ workspace: AdminWorkspace; reviewedAt: string; model: string; added: number; peopleAdded: number; warnings: string[] }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/review`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  },
  generateCollateral(workspaceId: string, suggestionId: string) {
    return request<{ workspace: AdminWorkspace; assetId: string; version: number }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/collateral/${encodeURIComponent(suggestionId)}/generate`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  },
  qualifyWorkspace(workspaceId: string, qualification: DealQualificationDraft, existingDealId?: string) {
    return request<{ workspace: AdminWorkspace; dealId: string; gmailDraftId: string; gmailDraftsUrl: string; warnings?: FollowUpWarning[] }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/qualify`, {
      method: 'POST',
      body: JSON.stringify({
        id: qualification.id,
        dealName: qualification.dealName,
        dealStage: qualification.dealStage,
        contacts: qualification.contacts,
        email: qualification.email,
        ...(existingDealId ? { existingDealId } : {}),
        // Worker recomputes this from the calculators server-side rather than
        // trusting it outright, per the registerWorkspaceQuoteRoutes-adjacent
        // recompute in the qualify route. Sent as a starting point + the
        // admin's own override signal.
        ...(qualification.estimatedAmount !== undefined ? { estimatedAmount: qualification.estimatedAmount } : {}),
        ...(qualification.pricingModel ? { pricingModel: qualification.pricingModel } : {}),
        ...(qualification.estimateSource ? { estimateSource: qualification.estimateSource } : {}),
      }),
    })
  },
  createFollowupDraft(workspaceId: string, followUp: DealFollowUpDraft) {
    return request<{ workspace: AdminWorkspace; gmailDraftId: string; gmailDraftsUrl: string; warnings?: FollowUpWarning[] }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/followup-draft`, {
      method: 'POST',
      body: JSON.stringify({ id: followUp.id, contacts: followUp.contacts, email: followUp.email }),
    })
  },
  loadTimeline(workspaceId: string, cursor?: string, source?: string) {
    const query = new URLSearchParams()
    if (cursor) query.set('cursor', cursor)
    if (source && source !== 'all') query.set('source', source)
    return request<{ events: TimelineEvent[]; nextCursor: string | null }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/timeline?${query}`)
  },
  upload(workspaceId: string, kind: 'logo' | 'asset', file: File, details?: { title?: string; description?: string; projectId?: string; section?: string; orderFormId?: string }) {
    const form = new FormData()
    form.set('kind', kind)
    form.set('file', file)
    if (details?.title) form.set('title', details.title)
    if (details?.description) form.set('description', details.description)
    if (details?.projectId) form.set('projectId', details.projectId)
    if (details?.section) form.set('section', details.section)
    if (details?.orderFormId) form.set('orderFormId', details.orderFormId)
    return request<{ id?: string; url: string; visibility?: 'pending' | 'published' }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uploads`, {
      method: 'POST',
      body: form,
      headers: {},
    })
  },
  createDemoCredentials(workspaceId: string, payload: { title: string; description: string | null; loginUrl: string; username: string; password: string }) {
    return request<{ id: string; visibility: 'pending' }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/demo-credentials`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },
}
